/**
 * Family Portal Hooks
 *
 * React Query hooks for family portal data fetching
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useApiClient } from '@/core/hooks';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';
import { createFamilyPortalApiService } from '../services';
import type { FamilyAccessSettings } from '../services';
import type { FamilyMember } from '@fuchsiahomecarecatalin/family-engagement';

/**
 * Hook to get family portal API service
 */
export function useFamilyPortalApi() {
  const apiClient = useApiClient();
  return useMemo(() => createFamilyPortalApiService(apiClient), [apiClient]);
}

/**
 * Hook to get family member profile
 */
export function useFamilyMemberProfile(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();

  return useQuery({
    queryKey: ['familyMember', familyMemberId],
    queryFn: () => familyPortalApi.getFamilyMemberProfile(familyMemberId!),
    enabled: !!familyMemberId,
  });
}

export function useUpdateFamilyMemberProfile(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: Partial<FamilyMember>) =>
      familyPortalApi.updateFamilyMemberProfile(familyMemberId!, profile),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['familyMember', familyMemberId] });
      toast.success('Profile settings saved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Unable to save profile settings');
    },
  });
}

/**
 * Hook to get family dashboard data
 */
export function useFamilyDashboard(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();

  return useQuery({
    queryKey: ['familyDashboard', familyMemberId],
    queryFn: () => familyPortalApi.getFamilyDashboard(familyMemberId!),
    enabled: !!familyMemberId,
    refetchInterval: 30000, // Poll every 30 seconds for real-time updates
  });
}

export function useFamilyAccessSettings(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();

  return useQuery({
    queryKey: ['familyAccessSettings', familyMemberId],
    queryFn: () => familyPortalApi.getFamilyAccessSettings(familyMemberId!),
    enabled: !!familyMemberId,
  });
}

export function useUpdateFamilyAccessSettings(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<FamilyAccessSettings>) =>
      familyPortalApi.updateFamilyAccessSettings(familyMemberId!, settings),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['familyAccessSettings', familyMemberId] });
      void queryClient.invalidateQueries({ queryKey: ['familyDashboard', familyMemberId] });
      toast.success('Family access settings saved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Unable to save family access settings');
    },
  });
}

export function useFamilyAcknowledgements(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();

  return useQuery({
    queryKey: ['familyAcknowledgements', familyMemberId],
    queryFn: () => familyPortalApi.getAcknowledgements(familyMemberId!),
    enabled: !!familyMemberId,
  });
}

export function useAcknowledgeFamilyItem(familyMemberId: UUID | null) {
  const familyPortalApi = useFamilyPortalApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { itemId: string; label: string; itemType: string }) =>
      familyPortalApi.acknowledgeItem(familyMemberId!, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['familyAcknowledgements', familyMemberId] });
      toast.success('Confirmed as received');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Unable to confirm receipt');
    },
  });
}

/**
 * Hook to get recent activity feed
 */
export function useRecentActivity(familyMemberId: UUID | null, limit = 20) {
  const familyPortalApi = useFamilyPortalApi();

  return useQuery({
    queryKey: ['activityFeed', familyMemberId, limit],
    queryFn: () => familyPortalApi.getRecentActivity(familyMemberId!, limit),
    enabled: !!familyMemberId,
    refetchInterval: 30000, // Poll every 30 seconds for real-time updates
  });
}
